import { Injectable, Compiler, NgModule, NO_ERRORS_SCHEMA } from '@angular/core';
import { RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { SemSharedModule } from '../../shared/modules/sem-shared/sem-shared.module';
import { SemNavigationModule } from '../../navigation/modules/sem-navigation/sem-navigation.module';

@Injectable({ providedIn: 'root' })
export class DynamicCompilationService {

  /**
   * Sanitize CMS-generated HTML for Angular 21's stricter template parser.
   * Currently strips <script> blocks which Angular forbids in templates.
   */
  static sanitizeTemplate(html: string): string {
    if (!html) return html;
    // Remove <script>...</script> blocks (Angular forbids them in templates)
    html = html.replace(/<script[\s\S]*?<\/script>/gi, '');
    return html;
  }

  constructor(private compiler: Compiler) {}

  /**
   * Compile a route module and return the module class for use with loadChildren.
   * We run compileModuleAsync to JIT-compile all imported declarations (registering inputs/outputs),
   * but return the module class instead of the NgModuleFactory for proper Angular 21 router handling.
   *
   * Deliberately NOT run via NgZone.runOutsideAngular: JIT compilation isn't perf-sensitive
   * enough to need it, and it added a needless zone hop into every dynamic route/control.
   */
  async createRouteModule(componentType: any, routes: any[]): Promise<any> {
    const tmpModule = NgModule({
      imports: [CommonModule, SemSharedModule, SemNavigationModule, RouterModule.forChild(routes)],
      declarations: [componentType],
      providers: [],
      schemas: [NO_ERRORS_SCHEMA]
    })(class {});

    // Use compileModuleAndAllComponentsAsync to force full JIT compilation of all
    // components (including inherited @Input() from parent classes like SemControlComponent)
    await this.compiler.compileModuleAndAllComponentsAsync(tmpModule);
    return tmpModule;
  }

  /**
   * Compile a module with a dynamic component. Returns ModuleWithComponentFactories.
   * The caller should pick the appropriate ComponentFactory from the result.
   */
  async compileComponentModule(
    componentType: any,
    config: {
      imports?: any[];
      exports?: any[];
      providers?: any[];
    } = {}
  ): Promise<any> {
    const tmpModule = NgModule({
      imports: config.imports || [CommonModule, SemSharedModule, SemNavigationModule],
      exports: config.exports || [],
      declarations: [componentType],
      providers: config.providers || [],
      schemas: [NO_ERRORS_SCHEMA]
    })(class {});

    return await this.compiler.compileModuleAndAllComponentsAsync(tmpModule);
  }
}
